import { makeObservable, observable, action } from "mobx";
import isEqual from "lodash/isEqual";
import type { SupportedUpdateOperations } from "@whatslly/types/tuvis-messaging/TuvisMessagingStream";
export class DataCollection<
  T extends { new (raw: any): any; cast?: (key: keyof InstanceType<T>, orig: InstanceType<T>[K]) => any },
  K extends keyof InstanceType<T>,
> {
  items: Map<InstanceType<T>[K], InstanceType<T>> = observable.map([], {});
  constructor(
    private readonly Factory: T,
    private readonly index: K
  ) {
    makeObservable(this, {
      items: observable.shallow,
      reset: action,
      insert: action,
      update: action,
      remove: action,
    });
  }

  /** @deprecated this is here only to support old REST calls */
  reset(items: Array<ConstructorParameters<T>[0]>) {
    this.items.clear();
    for (const item of items ?? []) {
      this.insert(item);
    }
  }

  get size() {
    return this.items.size;
  }

  [Symbol.iterator]() {
    return this.items.values();
  }
  get(id: InstanceType<T>[K]) {
    return this.items.get(id);
  }
  remove(id: InstanceType<T>[K]) {
    this.items.delete(id);
  }
  find(predicate: (item: InstanceType<T>) => boolean) {
    return Array.from(this).find(predicate);
  }
  filter(predicate: (item: InstanceType<T>) => boolean) {
    return Array.from(this).filter(predicate);
  }
  insert(item: ConstructorParameters<T>[0]) {
    this.items.set(item[this.index], new this.Factory(item));
  }
  update(
    selector: { [k in keyof ConstructorParameters<T>[0]]?: string | number | { $in: string[] } },
    { $max, $addToSet, ...data }: Partial<ConstructorParameters<T>[0] & SupportedUpdateOperations>,
    options?: { updateOne?: boolean }
  ) {
    const selectors = Object.entries(selector);
    const items = this.filter((item) =>
      selectors.every(([key, filter]) => (typeof filter === "object" ? filter?.$in.includes(item[key]) : filter === item[key]))
    ).slice(0, options?.updateOne ? 1 : undefined);

    for (const item of items) {
      Object.assign(item, data);
      if ($max) {
        Object.entries($max).forEach(([key, value]) =>
          Object.assign(item, { [key]: Math.max(this.Factory.cast?.(key, item[key]) ?? item[key], value as any) })
        );
      }
      if ($addToSet) {
        Object.entries($addToSet).forEach(([key, value]) => {
          if (item[key] instanceof Set) {
            item[key].add(value);
          } else if (Array.isArray(item[key]) && !item[key].some((v: any) => isEqual(v, value))) {
            item[key].push(value);
          }
        });
      }
    }
  }
}
